import React, { useRef } from 'react';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ColumnButton } from 'lib/components/ColumnButton';
import CheckboxInput from 'lib/components/Checkbox/CheckboxInput';
import { TextField } from 'lib/components/TextField';
import { MailDelivery } from 'lib/types';
import { State } from 'lib/enums';
import { RecipientMailingForm } from '../InvoiceRecipientFormComponents';

const EMPTY_ADDRESS = {
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_state: '',
  address_zip: ''
};

/** Whether two recipients share the same (non-empty) mailing address. */
function addressesMatch(a: MailDelivery, b: MailDelivery): boolean {
  const aa = a.address;
  const ba = b.address;
  return (
    (aa?.address_line1 ?? '').trim() !== '' &&
    (aa?.address_line1 ?? '') === (ba?.address_line1 ?? '') &&
    (aa?.address_line2 ?? '') === (ba?.address_line2 ?? '') &&
    (aa?.address_city ?? '') === (ba?.address_city ?? '') &&
    String(aa?.address_state ?? '') === String(ba?.address_state ?? '') &&
    (aa?.address_zip ?? '') === (ba?.address_zip ?? '')
  );
}

export type MailRecipientsSummary = {
  /** Sum of copies across all addresses. */
  totalCopies: number;
  /** Number of distinct addresses (= number of envelopes/labels). */
  addressCount: number;
};

export function getMailRecipientsSummary(
  recipients: MailDelivery[]
): MailRecipientsSummary {
  return {
    totalCopies: recipients.reduce((sum, r) => sum + (r.copies ?? 0), 0),
    addressCount: recipients.length
  };
}

type MailRecipientsManagerProps = {
  recipients: MailDelivery[];
  onChange: (recipients: MailDelivery[]) => void;
  /** Per-index address verification error messages. Shown only when truthy. */
  errors?: string[];
  /**
   * Pre-stamped template used for the phantom row (when recipients is empty)
   * and for every row added via "Add additional address". The caller is
   * responsible for setting documentType, shippingTier, and provider correctly
   * so this component never produces a MailDelivery missing those fields.
   */
  blankRow: MailDelivery;
  /**
   * When true, exactly one mailed copy is implied: the copies field and the
   * "Add additional address" affordance are hidden. The caller keeps the data
   * collapsed to a single copies-1 recipient.
   */
  lockToSingleCopy?: boolean;
  /**
   * When true, the first address is the free included affidavit: its count field
   * is hidden (one copy implied) while every additional address keeps a count
   * field for the paid affidavits sent there.
   */
  firstRowIsFreeCopy?: boolean;
  /** Label for the quantity field. Defaults to "Number of copies". */
  copiesLabel?: string;
};

/**
 * Controlled multi-address + per-address copies manager. State is managed
 * externally (via the deliveryRecipients slice); this component only renders
 * and reports edits.
 */
export function MailRecipientsManager({
  recipients,
  onChange,
  errors,
  blankRow,
  lockToSingleCopy = false,
  firstRowIsFreeCopy = false,
  copiesLabel = 'Number of copies'
}: MailRecipientsManagerProps) {
  // Stable keys: assign a monotonically increasing ID to each row so React
  // doesn't confuse DOM nodes when a middle row is removed.
  const nextId = useRef(0);
  const keyMapRef = useRef<number[]>([]);

  // Ensure at least one row is always shown. Use the caller-supplied blankRow
  // so the phantom carries the correct documentType/shippingTier/provider from
  // the start rather than relying on the onChange handler to repair it later.
  const displayRecipients =
    recipients.length > 0 ? recipients : [{ ...blankRow }];

  // Grow key array to match current list length
  while (keyMapRef.current.length < displayRecipients.length) {
    keyMapRef.current.push(nextId.current++);
  }
  // Shrink if items were removed from the end
  if (keyMapRef.current.length > displayRecipients.length) {
    keyMapRef.current.length = displayRecipients.length;
  }

  function updateRecipient(index: number, patch: Partial<MailDelivery>) {
    const updated = displayRecipients.map((r, i) =>
      i === index ? { ...r, ...patch } : r
    );
    onChange(updated);
  }

  function addRecipient() {
    keyMapRef.current.push(nextId.current++);
    onChange([...displayRecipients, { ...blankRow }]);
  }

  function removeRecipient(index: number) {
    keyMapRef.current.splice(index, 1);
    const updated = displayRecipients.filter((_, i) => i !== index);
    onChange(updated);
  }

  return (
    <div className="space-y-6">
      {displayRecipients.map((recipient, index) => {
        return (
          <div
            key={keyMapRef.current[index]}
            className="border border-column-gray-200 rounded-md p-4 space-y-3"
          >
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-medium text-column-gray-800">
                {index !== 0
                  ? `Additional address ${index}`
                  : firstRowIsFreeCopy
                  ? 'Mailing address for included affidavit'
                  : 'Mailing address'}
              </h4>
              {/* The free included copy (row 0 when firstRowIsFreeCopy) must not be removable */}
              {displayRecipients.length > 1 &&
                !(firstRowIsFreeCopy && index === 0) && (
                  <ColumnButton
                    id={`remove-address-${index}`}
                    type="button"
                    secondary
                    link
                    startIcon={<TrashIcon className="w-4 h-4" />}
                    buttonText="Remove"
                    onClick={() => removeRecipient(index)}
                  />
                )}
            </div>

            {/* Additional wet-sig rows can reuse the included copy's address. */}
            {firstRowIsFreeCopy && index > 0 && (
              <CheckboxInput
                id={`same-as-included-${index}`}
                labelText="Send this additional copy to the same mailing address as the included affidavit"
                checked={addressesMatch(recipient, displayRecipients[0])}
                onChange={() => {
                  const included = displayRecipients[0];
                  const nowMatches = addressesMatch(recipient, included);
                  updateRecipient(index, {
                    isNoticeTypeDefault: false,
                    name: nowMatches ? '' : included.name,
                    address: nowMatches
                      ? { ...EMPTY_ADDRESS }
                      : { ...EMPTY_ADDRESS, ...included.address }
                  });
                }}
              />
            )}

            <TextField
              id={`recipient-name-${index}`}
              labelText="Recipient name"
              maxLength={40}
              value={recipient.name}
              onChange={v =>
                updateRecipient(index, { name: v, isNoticeTypeDefault: false })
              }
            />

            <RecipientMailingForm
              addressLine1={recipient.address?.address_line1 ?? ''}
              setAddressLine1={v =>
                updateRecipient(index, {
                  address: { ...recipient.address, address_line1: v },
                  isNoticeTypeDefault: false
                })
              }
              addressLine2={recipient.address?.address_line2 ?? ''}
              setAddressLine2={v =>
                updateRecipient(index, {
                  address: { ...recipient.address, address_line2: v }
                })
              }
              setCity={v =>
                updateRecipient(index, {
                  address: { ...recipient.address, address_city: v }
                })
              }
              city={recipient.address?.address_city ?? ''}
              // The delivery-recipients subsystem stores address_state as the
              // numeric State enum value, but RecipientMailingForm's select works
              // in labels. Convert value -> label on read and label -> value on
              // write so the dropdown autopopulates and storage stays canonical.
              state={
                State.by_value(recipient.address?.address_state)?.label ??
                recipient.address?.address_state?.toString() ??
                ''
              }
              setState={v =>
                updateRecipient(index, {
                  address: {
                    ...recipient.address,
                    address_state: State.by_label(v)?.value ?? v
                  }
                })
              }
              zip={recipient.address?.address_zip ?? ''}
              setZip={v =>
                updateRecipient(index, {
                  address: { ...recipient.address, address_zip: v }
                })
              }
            />

            {!!errors?.[index] && (
              <p className="text-sm text-red-600">{errors[index]}</p>
            )}

            {/* Hidden for a single implied copy, and for the free included
                copy's own address (row 0 when firstRowIsFreeCopy). */}
            {!lockToSingleCopy && !(firstRowIsFreeCopy && index === 0) && (
              <div className="w-40">
                <TextField
                  id={`copies-${index}`}
                  type="number"
                  min={1}
                  step={1}
                  labelText={copiesLabel}
                  value={String(recipient.copies ?? 1)}
                  onChange={v => {
                    const parsed = parseInt(v, 10);
                    updateRecipient(index, {
                      copies: Number.isNaN(parsed) || parsed < 1 ? 1 : parsed
                    });
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      {!lockToSingleCopy && (
        <ColumnButton
          id="add-address"
          type="button"
          secondary
          link
          startIcon={<PlusCircleIcon className="w-5 h-5" />}
          buttonText="Add additional address"
          onClick={addRecipient}
        />
      )}
    </div>
  );
}
